import { ThemeEvents } from "@theme/events";

/**
 * Displays a product price and reacts to variant changes.
 *
 * Listens on `document` for `variant:update` events so it works regardless of
 * where the variant picker lives in the DOM (e.g. inside a drawer/modal that
 * sits outside the .shopify-section).
 *
 * On variant change the component finds its counterpart in the freshly fetched
 * section HTML and swaps its own content, keeping all formatting server-side.
 *
 * Custom sizes have no variant to fetch, so their price is pushed in from
 * product-purchase-options via `showCustomPrice()` / `restore()`.
 */
class ProductPriceDisplay extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @param {import("@theme/events").VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    if (!resource || !data?.html) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId !== this.dataset.productId) {
      return;
    }

    const updated = data.html.querySelector(`product-price-display[data-product-id="${this.dataset.productId}"]`);
    if (!updated) return;

    this.innerHTML = updated.innerHTML;
  };

  /**
   * Shows a calculated custom size price instead of the server-rendered one.
   * @param {string} price - Formatted money string
   * @param {boolean} [showFrom] - Prefix with "From" while the price is the untouched minimum
   */
  showCustomPrice(price, showFrom = false) {
    const amount = this.querySelector("[data-computed-price-amount]");
    if (!amount) return;

    amount.textContent = price;
    this.querySelector("[data-computed-price-from]")?.classList.toggle("hidden", !showFrom);
    this.#showComputed(true);
  }

  /** Shows the server-rendered price again. */
  restore() {
    this.#showComputed(false);
  }

  /** @param {boolean} computedVisible */
  #showComputed(computedVisible) {
    const computed = this.querySelector("[data-computed-price]");
    computed?.classList.toggle("hidden", !computedVisible);
    computed?.classList.toggle("flex", computedVisible);

    const server = this.querySelector("[data-server-price]");
    server?.classList.toggle("hidden", computedVisible);
    server?.classList.toggle("flex", !computedVisible);
  }
}

if (!customElements.get("product-price-display")) {
  customElements.define("product-price-display", ProductPriceDisplay);
}
